// src/components/Dashboard_ADM/Atendimento/FluxosTab.tsx
// Sequências configuráveis de mensagem por gatilho (texto/imagem/documento/vídeo) — seção 8.4
// GET/PATCH /whatsapp-agent/flows | POST /whatsapp-agent/flows/upload-media

import React, { useState, useEffect, useCallback } from 'react';
import {
  Workflow, Loader2, RefreshCw, Save, Plus, Trash2, ArrowUp, ArrowDown,
  Upload, Image as ImageIcon, FileText, Video, Type,
} from 'lucide-react';
import apiClient from '../../../services/api';
import { API_BASE, authH } from '../../../utils/apiConfig';

type FlowStepType = 'text' | 'image' | 'document' | 'video';

interface FlowStep {
  order: number;
  type: FlowStepType;
  text?: string;
  mediaUrl?: string;
  caption?: string;
  delaySeconds?: number;
}

interface FlowTrigger {
  id: string;
  triggerKey: string;
  label: string;
  active: boolean;
  steps: FlowStep[];
}

const STEP_TYPE_ICONS: Record<FlowStepType, typeof Type> = {
  text: Type, image: ImageIcon, document: FileText, video: Video,
};

const VARIAVEIS_DISPONIVEIS =
  '{{nome}} {{schoolName}} {{systemId}} {{registroFepcssp}} {{professionalRegistro}} ' +
  '{{accessLogin}} {{accessPassword}} {{paymentLinkUrl}}';

const FluxosTab: React.FC = () => {
  const [flows, setFlows] = useState<FlowTrigger[]>([]);
  const [loading, setLoading] = useState(true);
  const [salvandoKey, setSalvandoKey] = useState<string | null>(null);
  const [uploadingStep, setUploadingStep] = useState<string | null>(null);

  const fetchFlows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get<FlowTrigger[]>('/whatsapp-agent/flows');
      setFlows(data ?? []);
    } catch {
      setFlows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFlows(); }, [fetchFlows]);

  const updateFlow = (triggerKey: string, patch: Partial<FlowTrigger>) => {
    setFlows((prev) => prev.map((f) => (f.triggerKey === triggerKey ? { ...f, ...patch } : f)));
  };

  const updateStep = (triggerKey: string, index: number, patch: Partial<FlowStep>) => {
    setFlows((prev) =>
      prev.map((f) => {
        if (f.triggerKey !== triggerKey) return f;
        return { ...f, steps: f.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) };
      }),
    );
  };

  const addStep = (triggerKey: string) => {
    setFlows((prev) =>
      prev.map((f) => {
        if (f.triggerKey !== triggerKey) return f;
        const nextOrder = f.steps.length > 0 ? Math.max(...f.steps.map((s) => s.order)) + 1 : 1;
        return { ...f, steps: [...f.steps, { order: nextOrder, type: 'text', text: '' }] };
      }),
    );
  };

  const removeStep = (triggerKey: string, index: number) => {
    setFlows((prev) =>
      prev.map((f) => {
        if (f.triggerKey !== triggerKey) return f;
        return {
          ...f,
          steps: f.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 })),
        };
      }),
    );
  };

  const moveStep = (triggerKey: string, index: number, direction: -1 | 1) => {
    setFlows((prev) =>
      prev.map((f) => {
        if (f.triggerKey !== triggerKey) return f;
        const steps = [...f.steps];
        const target = index + direction;
        if (target < 0 || target >= steps.length) return f;
        [steps[index], steps[target]] = [steps[target], steps[index]];
        return { ...f, steps: steps.map((s, i) => ({ ...s, order: i + 1 })) };
      }),
    );
  };

  const handleUpload = async (triggerKey: string, index: number, file: File) => {
    const key = `${triggerKey}-${index}`;
    setUploadingStep(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/whatsapp-agent/flows/upload-media`, {
        method: 'POST',
        headers: authH(),
        body: fd,
      });
      if (!res.ok) throw new Error('Falha no upload');
      const data = await res.json();
      updateStep(triggerKey, index, { mediaUrl: data.url });
    } catch {
      alert('Erro ao enviar arquivo');
    } finally {
      setUploadingStep(null);
    }
  };

  const handleSalvar = async (flow: FlowTrigger) => {
    setSalvandoKey(flow.triggerKey);
    try {
      await apiClient.patch(`/whatsapp-agent/flows/${flow.triggerKey}`, {
        label: flow.label,
        active: flow.active,
        steps: flow.steps,
      });
    } catch {
      alert('Erro ao salvar fluxo');
    } finally {
      setSalvandoKey(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1 flex items-center gap-3">
              <Workflow className="w-6 h-6 text-blue-600" /> Fluxos de Mensagem
            </h2>
            <p className="text-gray-600">
              Sequências de texto/imagem/vídeo/documento enviadas automaticamente por etapa do funil.
              Variáveis disponíveis: <code className="text-xs bg-gray-100 px-1 rounded">{VARIAVEIS_DISPONIVEIS}</code>
            </p>
          </div>
          <button onClick={fetchFlows}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 flex-shrink-0">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-blue-600 animate-spin" /></div>
      ) : flows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-500">
          Nenhum gatilho encontrado — rode a migration do banco.
        </div>
      ) : (
        <div className="space-y-4">
          {flows.map((flow) => (
            <div key={flow.triggerKey} className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4 gap-3">
                <input value={flow.label} onChange={(e) => updateFlow(flow.triggerKey, { label: e.target.value })}
                  className="font-black text-gray-900 text-lg border-b-2 border-transparent focus:border-blue-500 outline-none flex-1" />
                <label className="flex items-center gap-2 text-sm font-bold text-gray-600 flex-shrink-0">
                  <input type="checkbox" checked={flow.active}
                    onChange={(e) => updateFlow(flow.triggerKey, { active: e.target.checked })}
                    className="w-4 h-4" />
                  Ativo
                </label>
              </div>

              <div className="space-y-3">
                {flow.steps.map((step, index) => {
                  const Icon = STEP_TYPE_ICONS[step.type];
                  const uploadKey = `${flow.triggerKey}-${index}`;
                  return (
                    <div key={index} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-gray-500" />
                          <select value={step.type}
                            onChange={(e) => updateStep(flow.triggerKey, index, { type: e.target.value as FlowStepType })}
                            className="px-2 py-1 border border-gray-300 rounded-lg text-sm font-bold text-gray-700">
                            <option value="text">Texto</option>
                            <option value="image">Imagem</option>
                            <option value="document">Documento</option>
                            <option value="video">Vídeo</option>
                          </select>
                          <span className="text-xs text-gray-400">passo {index + 1}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => moveStep(flow.triggerKey, index, -1)} disabled={index === 0}
                            className="p-1.5 text-gray-500 hover:bg-gray-200 rounded-lg disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                          <button onClick={() => moveStep(flow.triggerKey, index, 1)} disabled={index === flow.steps.length - 1}
                            className="p-1.5 text-gray-500 hover:bg-gray-200 rounded-lg disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                          <button onClick={() => removeStep(flow.triggerKey, index)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>

                      {step.type === 'text' ? (
                        <textarea value={step.text ?? ''} rows={3}
                          onChange={(e) => updateStep(flow.triggerKey, index, { text: e.target.value })}
                          placeholder="Texto da mensagem — use {{variaveis}} disponíveis acima"
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm text-gray-900 resize-none" />
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <input value={step.mediaUrl ?? ''} readOnly
                              placeholder="Nenhum arquivo enviado ainda"
                              className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg text-sm text-gray-500 bg-white" />
                            <label className="flex items-center gap-1 px-3 py-2 rounded-lg border-2 border-gray-300 text-sm font-bold text-gray-700 cursor-pointer hover:bg-gray-100 flex-shrink-0">
                              {uploadingStep === uploadKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                              Enviar arquivo
                              <input type="file" className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleUpload(flow.triggerKey, index, file);
                                }} />
                            </label>
                          </div>
                          <input value={step.caption ?? ''}
                            onChange={(e) => updateStep(flow.triggerKey, index, { caption: e.target.value })}
                            placeholder="Legenda (opcional) — também aceita {{variaveis}}"
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm text-gray-900" />
                        </div>
                      )}

                      <div className="flex items-center gap-2 mt-2">
                        <label className="text-xs font-bold text-gray-500">Aguardar antes de enviar (segundos):</label>
                        <input type="number" min={0} value={step.delaySeconds ?? 0}
                          onChange={(e) => updateStep(flow.triggerKey, index, { delaySeconds: Number(e.target.value) || 0 })}
                          className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm text-gray-900" />
                      </div>
                    </div>
                  );
                })}

                <button onClick={() => addStep(flow.triggerKey)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 text-sm font-bold w-full justify-center">
                  <Plus className="w-4 h-4" /> Adicionar passo
                </button>
              </div>

              <button onClick={() => handleSalvar(flow)} disabled={salvandoKey === flow.triggerKey}
                className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-50">
                {salvandoKey === flow.triggerKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FluxosTab;
